import { supabase } from './supabase'
import { 
  DecisionOption, 
  DecisionCriteria, 
  AnalysisResult, 
  CausalChainNode, 
  ContextProfile, 
  MultiOptionAnalysis 
} from '../types'

export interface AnalysisHistoryEntry {
  id: string
  user_id: string
  title: string
  description: string
  analysis_type: 'pros-cons' | 'context' | 'multi-option' | 'causal-chain'
  options: DecisionOption[]
  criteria: DecisionCriteria[]
  result: AnalysisResult | CausalChainNode | MultiOptionAnalysis | null
  context_profile?: ContextProfile
  created_at: string
  updated_at: string
}

export interface CreateAnalysisHistoryData {
  title: string
  description: string
  analysis_type: 'pros-cons' | 'context' | 'multi-option' | 'causal-chain'
  options: DecisionOption[]
  criteria: DecisionCriteria[]
  result: AnalysisResult | CausalChainNode | MultiOptionAnalysis | null
  context_profile?: ContextProfile
}

export class AnalysisHistoryService {
  // Get user analysis history
  static async getUserHistory(limit = 50, offset = 0): Promise<AnalysisHistoryEntry[]> {
    try {
      const { data, error } = await supabase
        .from('analysis_history')
        .select('*')
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1)

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching analysis history:', error)
      throw error
    }
  }

  // Get analysis history by type
  static async getHistoryByType(
    analysisType: string, 
    limit = 50, 
    offset = 0
  ): Promise<AnalysisHistoryEntry[]> {
    try {
      const { data, error } = await supabase
        .from('analysis_history')
        .select('*')
        .eq('analysis_type', analysisType)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1)

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching analysis history by type:', error)
      throw error
    }
  }

  // Get single analysis history detail
  static async getHistoryById(id: string): Promise<AnalysisHistoryEntry | null> {
    try {
      const { data, error } = await supabase
        .from('analysis_history')
        .select('*')
        .eq('id', id)
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error fetching analysis history by id:', error)
      throw error
    }
  }

  // Create new analysis history
  static async createHistory(historyData: CreateAnalysisHistoryData): Promise<AnalysisHistoryEntry> {
    try {
      const { data, error } = await supabase
        .from('analysis_history')
        .insert([historyData])
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error creating analysis history:', error)
      throw error
    }
  }

  // Update analysis history
  static async updateHistory(
    id: string, 
    updates: Partial<CreateAnalysisHistoryData>
  ): Promise<AnalysisHistoryEntry> {
    try {
      const { data, error } = await supabase
        .from('analysis_history')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data
    } catch (error) {
      console.error('Error updating analysis history:', error)
      throw error
    }
  }

  // Delete analysis history
  static async deleteHistory(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('analysis_history')
        .delete()
        .eq('id', id)

      if (error) throw error
    } catch (error) {
      console.error('Error deleting analysis history:', error)
      throw error
    }
  }

  // Get analysis history statistics
  static async getHistoryStats(): Promise<{
    total: number
    byType: Record<string, number>
  }> {
    try {
      // Get total count
      const { count: total, error: totalError } = await supabase
        .from('analysis_history')
        .select('*', { count: 'exact', head: true })

      if (totalError) throw totalError

      // Get count by type
      const { data: typeData, error: typeError } = await supabase
        .from('analysis_history')
        .select('analysis_type')

      if (typeError) throw typeError

      const byType = typeData?.reduce((acc, item) => {
        acc[item.analysis_type] = (acc[item.analysis_type] || 0) + 1
        return acc
      }, {} as Record<string, number>) || {}

      return {
        total: total || 0,
        byType
      }
    } catch (error) {
      console.error('Error fetching history stats:', error)
      throw error
    }
  }

  // Search analysis history
  static async searchHistory(
    query: string, 
    limit = 20
  ): Promise<AnalysisHistoryEntry[]> {
    try {
      const { data, error } = await supabase
        .from('analysis_history')
        .select('*')
        .or(`title.ilike.%${query}%,description.ilike.%${query}%`)
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error searching analysis history:', error)
      throw error
    }
  }
} 